import styled from "styled-components/macro";
import {
  TREND_CHART_BAR_PADDING_HZ,
  TREND_CHART_BAR_WIDTH,
} from "../../../TrendChartStyles";
import type {
  RollupWithTagsAndCountsAndRange,
  TimeRangeFinite,
} from "types/types";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useTrendLineSegment } from "./trendLineUtils";
import { useIsDarkMode } from "store/atoms";
import { darkModeTextColor } from "constants/colors";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

// consists of a ball in the middle of the stack,
// and a stick connecting the current ball to the previous ball
type TrendLineSegmentProps = {
  barHeight: number;
  barRange: TimeRangeFinite;
  rollupMetric: RollupWithTagsAndCountsAndRange;
  prevRollupMetric: RollupWithTagsAndCountsAndRange | null;
};
// tslint:disable-next-line: cognitive-complexity
export default function TrendLineSegment({
  barHeight,
  barRange,
  rollupMetric,
  prevRollupMetric,
}: TrendLineSegmentProps) {
  const {
    shouldShowTargetTrendLine,
    shouldShowTrendLine,
    ballHeightPx,
    prevBallHeightPx,
    targetLineHeight,
    prevTargetLineHeight,
  } = useTrendLineSegment({
    rollupMetric,
    barRange,
    barHeight,
    prevRollupMetric,
  });
  return (
    <>
      {shouldShowTargetTrendLine && targetLineHeight ? (
        // target line (dashed lines)
        <TrendLineSegmentContent
          ballHeightPx={targetLineHeight}
          prevBallHeightPx={prevTargetLineHeight}
          number={null}
        />
      ) : null}
      {shouldShowTrendLine ? (
        // trend line (solid, ball-and-stick)
        <TrendLineSegmentContent
          ballHeightPx={ballHeightPx}
          prevBallHeightPx={prevBallHeightPx}
          number={rollupMetric?.cumulativeOutput || 0}
        />
      ) : null}
    </>
  );
}

// consists of a ball in the middle of the stack,
// and a stick connecting the current ball to the previous ball
type TrendLineSegmentContentProps = {
  ballHeightPx: number;
  prevBallHeightPx: number | null;
  number: number | null;
};

function TrendLineSegmentContent({
  ballHeightPx,
  prevBallHeightPx,
  number,
}: TrendLineSegmentContentProps) {
  // if we don't show a number, it's the target line
  const isTargetTrendLine = number === null;

  const distanceBetweenBarsPx =
    TREND_CHART_BAR_WIDTH + 2 * TREND_CHART_BAR_PADDING_HZ;

  // previous point is to the left of the current point by one bar's width
  const prevPoint = { x: 0, y: prevBallHeightPx };
  const thisPoint = { x: distanceBetweenBarsPx, y: ballHeightPx };

  const stickLength =
    prevBallHeightPx &&
    getDistanceBetweenTwoPoints(prevPoint as Point, thisPoint);

  const stickAngle = getAngleBetweenTwoPoints(prevPoint as Point, thisPoint);

  const [
    {
      general: { enableLotInfoModule },
    },
  ] = useFlightConfig();

  // if the segment is near zero, flip the number up above the ball
  const isNearZero = ballHeightPx < BALL_RADIUS * 5;
  const [isDarkMode] = useIsDarkMode();
  return (
    <TrendLineSegmentStyles
      {...(enableLotInfoModule
        ? { "data-server-names": [SERVER_NAMES.midgardDataDumpSinceLotStart] }
        : {})}
      {...{
        $ballHeightPx: ballHeightPx,
        $stickLength: stickLength,
        $stickAngle: stickAngle,
        $isNearZero: isNearZero,
        $isTargetTrendLine: isTargetTrendLine,
        $isDarkMode: isDarkMode,
      }}
    >
      <div className="ballAndStick">
        {prevBallHeightPx !== null && <div className="stick" />}
        {!isTargetTrendLine && (
          <div className="ballAndNumber">
            <div className="ball" />
            <div className="number background">{number}</div>
            <div className="number">{number}</div>
          </div>
        )}
      </div>
    </TrendLineSegmentStyles>
  );
}

type Point = { x: number; y: number };

function getDistanceBetweenTwoPoints(p1: Point, p2: Point) {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}
function getAngleBetweenTwoPoints(p1: Point, p2: Point) {
  // https://gist.github.com/conorbuck/2606166
  return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
}

const BALL_RADIUS = 4;
const BALL_WIDTH = BALL_RADIUS * 2;
const STICK_HEIGHT = 1;
const DASH_WIDTH = 20;
const TARGET_TREND_LINE_BACKGROUND = `repeating-linear-gradient(
  90deg,
  #000000,
  #000000 ${DASH_WIDTH}px,
  transparent ${DASH_WIDTH}px,
  transparent ${DASH_WIDTH * 2}px
  )`;

const TrendLineSegmentStyles = styled.div<{
  $isDarkMode: boolean;
  $ballHeightPx: number;
  $stickLength: number | null;
  $stickAngle: number;
  $isNearZero: boolean;
  $isTargetTrendLine: boolean;
}>`
  position: absolute;
  bottom: 0;
  left: 50%;
  right: 50%;
  .ballAndStick {
    position: absolute;
    left: ${-BALL_RADIUS}px;
    top: ${props => -BALL_RADIUS - props.$ballHeightPx}px;
    .ballAndNumber {
      height: 0;
      width: 0;
      position: relative;
      z-index: 2;
      .ball {
        width: ${BALL_WIDTH}px;
        height: ${BALL_WIDTH}px;
        background: ${({ $isDarkMode }) => ($isDarkMode ? "white" : "black")};
        border-radius: 100%;
        position: absolute;
      }
    }
    .stick {
      position: absolute;
      z-index: 0;
      right: ${props => (props.$isTargetTrendLine ? 0 : -BALL_RADIUS)}px;
      top: ${BALL_RADIUS - STICK_HEIGHT / 2}px;
      width: ${props =>
        (props.$stickLength ?? 0) -
        (props.$isTargetTrendLine ? 0 : BALL_RADIUS)}px;
      height: ${props => (props.$isTargetTrendLine ? 2 : STICK_HEIGHT)}px;
      background: ${props =>
        props.$isTargetTrendLine
          ? TARGET_TREND_LINE_BACKGROUND
          : "hsl(0, 0%, 60%)"};
      transform-origin: right;
      transform: rotate(${props => -props.$stickAngle}deg);
    }
    .number {
      position: absolute;
      width: ${TREND_CHART_BAR_WIDTH}px;
      left: -${TREND_CHART_BAR_WIDTH / 2 - BALL_RADIUS}px;
      top: ${props => (props.$isNearZero ? -5 : 1) * BALL_RADIUS}px;
      color: ${({ $isDarkMode }) =>
        $isDarkMode ? darkModeTextColor : "black"};
      &.background {
        font-weight: bold;
        color: white;
        text-shadow:
          0px 0px 4px ${({ $isDarkMode }) => ($isDarkMode ? "black" : "white")},
          0px 0px 4px ${({ $isDarkMode }) => ($isDarkMode ? "black" : "white")}; /* double shadow for increased opacity */
      }
    }
  }
`;
